// ─────────────────────────────────────────────
// hooks/useAuth.js
// Authentication state and role management
// Uses: @supabase/supabase-js (MIT)
// License: MIT (your original IP)
// ─────────────────────────────────────────────

import { useState, useEffect } from "react";
import { supabase, getRole } from "../services/supabaseService";

export function useAuth() {
  const [session, setSession]               = useState(null);
  // Initial role is null (loading) rather than "resident" so the
  // /haulers wrong-surface gate does not fire on the transient
  // pre-RPC state for valid Hauler/admin sessions. Resolves to
  // "admin"|"driver"|"resident" once get_my_role() completes.
  const [role, setRole]                     = useState(null);
  const [authLoading, setAuthLoading]       = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [newPass,         setNewPass]         = useState("");
  const [newPassConfirm,  setNewPassConfirm]  = useState("");
  const [newPassBusy,     setNewPassBusy]     = useState(false);
  const [newPassDone,     setNewPassDone]     = useState(false);

  async function loadRole(s) {
    const r = await getRole(s);
    setRole(r === "admin" || r === "driver" ? r : "resident");
  }

  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    const isRecovery = hash.includes("type=recovery") || hash.includes("type=signup")
      || search.includes("type=recovery") || search.includes("type=signup");

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, sess) => {
      if (event === "PASSWORD_RECOVERY") {
        setPasswordRecovery(true);
        setAuthLoading(false);
        return;
      }
      if (isRecovery && event === "SIGNED_IN" && !newPassDone) {
        setPasswordRecovery(true);
        setAuthLoading(false);
        return;
      }
      setSession(sess);
      if (sess) loadRole(sess);
      setAuthLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session: sess } }) => {
      if (isRecovery) {
        setPasswordRecovery(true);
        setAuthLoading(false);
        return;
      }
      setSession(sess);
      if (sess) loadRole(sess);
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const isAdmin    = role === "admin";
  const isDriver   = role === "driver";
  const isElevated = isAdmin || isDriver;

  return {
    session,
    role,
    isAdmin,
    isDriver,
    isElevated,
    authLoading,
    passwordRecovery,
    setPasswordRecovery,
    newPass,
    setNewPass,
    newPassConfirm,
    setNewPassConfirm,
    newPassBusy,
    setNewPassBusy,
    newPassDone,
    setNewPassDone,
    setSession,
    setRole,
  };
}
